import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  X,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link2,
  Code2,
  Palette,
  Heading,
  Video,
  Undo2,
  Redo2,
  ALargeSmall,
} from 'lucide-react';
import './DefeitosVerveRichTextContent.css';
import './DefeitosVerveRichTextModal.css';

const RT_FORMAT_CMDS = ['bold', 'italic', 'underline', 'strikeThrough', 'insertUnorderedList', 'insertOrderedList'];

const RT_TEXT_COLORS = [
  { id: 'default', label: 'Texto padrão', color: '#18181b' },
  { id: 'gray', label: 'Texto cinza', color: '#787774' },
  { id: 'brown', label: 'Texto marrom', color: '#9f6b53' },
  { id: 'orange', label: 'Texto laranja', color: '#d9730d' },
  { id: 'yellow', label: 'Texto amarelo', color: '#cb912f' },
  { id: 'green', label: 'Texto verde', color: '#448361' },
  { id: 'blue', label: 'Texto azul', color: '#337ea9' },
  { id: 'purple', label: 'Texto roxo', color: '#9065b0' },
  { id: 'pink', label: 'Texto rosa', color: '#c14c8a' },
  { id: 'red', label: 'Texto vermelho', color: '#d44c47' },
];

const RT_BG_COLORS = [
  { id: 'default', label: 'Fundo padrão', color: 'transparent' },
  { id: 'gray', label: 'Fundo cinza', color: '#f1f1ef' },
  { id: 'brown', label: 'Fundo marrom', color: '#f4eeee' },
  { id: 'orange', label: 'Fundo laranja', color: '#fbecdd' },
  { id: 'yellow', label: 'Fundo amarelo', color: '#fbf3db' },
  { id: 'green', label: 'Fundo verde', color: '#edf3ec' },
  { id: 'blue', label: 'Fundo azul', color: '#e7f3f8' },
  { id: 'purple', label: 'Fundo roxo', color: '#f6f3f9' },
  { id: 'pink', label: 'Fundo rosa', color: '#faf1f5' },
  { id: 'red', label: 'Fundo vermelho', color: '#fdebec' },
];

const RT_HEADINGS = [
  { tag: 'h1', label: 'Título 1', preview: 'H1' },
  { tag: 'h2', label: 'Título 2', preview: 'H2' },
  { tag: 'h3', label: 'Título 3', preview: 'H3' },
  { tag: 'h4', label: 'Título 4', preview: 'H4' },
];

const RT_FONT_SIZES = [
  { id: 'medium', label: 'Médio', preview: '14', size: '14px' },
  { id: 'small', label: 'Pequeno', preview: '12', size: '12px' },
  { id: 'xs', label: 'Muito pequeno', preview: '10', size: '10px' },
];

const RT_DEFAULT_FONT_SIZE = '12px';
const RT_DEFAULT_FONT_SIZE_ID = 'small';

const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4'];

function isAllowedYoutubeIframeSrc(src) {
  if (!src || typeof src !== 'string') return false;
  try {
    const s = src.trim().replace(/^\/\//, 'https://');
    const u = new URL(s, 'https://www.youtube.com');
    const h = u.hostname.replace(/^www\./, '');
    return h === 'youtube.com' || h === 'youtube-nocookie.com';
  } catch {
    return false;
  }
}

function youtubeUrlToIframeHtml(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  let id = '';
  const m1 = raw.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
  const m2 = raw.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
  const m3 = raw.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
  const m4 = raw.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  id = (m1 || m2 || m3 || m4)?.[1] || '';
  if (!id) return '';
  const src = `https://www.youtube.com/embed/${id}`;
  return `<iframe class="defeitos-vv-media-iframe" width="560" height="315" src="${src}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen="allowfullscreen"></iframe>`;
}

function extractYoutubeIframeFromEmbedHtml(html) {
  const d = document.createElement('div');
  d.innerHTML = String(html || '').trim();
  const ifr = d.querySelector('iframe');
  if (!ifr) return '';
  const src = ifr.getAttribute('src');
  if (!isAllowedYoutubeIframeSrc(src)) return '';
  ifr.setAttribute('class', 'defeitos-vv-media-iframe');
  ifr.setAttribute('width', '560');
  ifr.setAttribute('height', '315');
  ifr.setAttribute('frameborder', '0');
  if (!ifr.getAttribute('allow')) {
    ifr.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
  }
  ifr.setAttribute('allowfullscreen', 'allowfullscreen');
  return ifr.outerHTML;
}

function buildMediaWrapHtml(iframeHtml) {
  return `<div class="defeitos-vv-media-wrap" contenteditable="false" data-defeitos-media="1" style="width:560px;max-width:100%;"><div class="defeitos-vv-media-frame">${iframeHtml}</div></div>`;
}

function stripRichTextEditorArtifacts(html) {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  d.querySelectorAll('.defeitos-vv-media-resize-handles').forEach((n) => n.remove());
  d.querySelectorAll('.defeitos-vv-media-shield').forEach((n) => n.remove());
  d.querySelectorAll('.defeitos-vv-media-wrap').forEach((n) => n.classList.remove('defeitos-vv-media-wrap--selected'));
  return d.innerHTML;
}

function enhanceRichTextMediaInEditor(rootEl) {
  if (!rootEl) return;
  rootEl.querySelectorAll('.defeitos-vv-media-wrap').forEach((wrap) => {
    const frame = wrap.querySelector('.defeitos-vv-media-frame');
    if (!frame || frame.querySelector('.defeitos-vv-media-shield')) return;
    const shield = document.createElement('div');
    shield.className = 'defeitos-vv-media-shield';
    shield.contentEditable = 'false';
    frame.appendChild(shield);
    ensureEditableAfterMedia(rootEl, wrap);
  });
}

function isEmptyEditableBlock(node) {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
  const tag = node.tagName.toLowerCase();
  if (!['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return false;
  const text = (node.textContent || '').replace(/\u200b/g, '').trim();
  if (text) return false;
  return node.childNodes.length === 0 || (node.childNodes.length === 1 && node.firstChild?.nodeName === 'BR');
}

function createEditablePlaceholder() {
  const p = document.createElement('p');
  p.appendChild(document.createElement('br'));
  return p;
}

function ensureEditableAfterMedia(rootEl, wrap) {
  if (!wrap || !rootEl || !wrap.parentNode) return null;
  let next = wrap.nextSibling;
  while (next && next.nodeType === Node.TEXT_NODE && !next.textContent.trim()) {
    next = next.nextSibling;
  }
  if (!next || next.classList?.contains('defeitos-vv-media-wrap')) {
    const p = createEditablePlaceholder();
    wrap.parentNode.insertBefore(p, wrap.nextSibling);
    return p;
  }
  if (next.nodeType === Node.ELEMENT_NODE && isEmptyEditableBlock(next)) {
    return next;
  }
  if (next.nodeType === Node.ELEMENT_NODE && !next.classList?.contains('defeitos-vv-media-wrap')) {
    return next;
  }
  const p = createEditablePlaceholder();
  wrap.parentNode.insertBefore(p, wrap.nextSibling);
  return p;
}

function placeCaretInBlockAfterMedia(rootEl, wrap) {
  if (!rootEl || !wrap) return;
  const block = ensureEditableAfterMedia(rootEl, wrap);
  rootEl.focus();
  const range = document.createRange();
  if (block) {
    const br = block.querySelector('br');
    if (br && block.childNodes.length === 1) {
      range.setStartBefore(br);
    } else {
      range.setStart(block, 0);
    }
  } else {
    range.setStartAfter(wrap);
  }
  range.collapse(true);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function placeCaretBeforeMedia(rootEl, wrap) {
  if (!rootEl || !wrap) return;
  rootEl.focus();
  const range = document.createRange();
  let prev = wrap.previousSibling;
  while (prev && prev.nodeType === Node.TEXT_NODE && !prev.textContent.trim()) {
    prev = prev.previousSibling;
  }
  if (prev && prev.nodeType === Node.ELEMENT_NODE && !prev.classList?.contains('defeitos-vv-media-wrap')) {
    range.selectNodeContents(prev);
    range.collapse(false);
  } else {
    const p = createEditablePlaceholder();
    wrap.parentNode.insertBefore(p, wrap);
    range.setStart(p, 0);
    range.collapse(true);
  }
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function clearMediaSelection(rootEl) {
  if (!rootEl) return;
  rootEl.querySelectorAll('.defeitos-vv-media-resize-handles').forEach((n) => n.remove());
  rootEl.querySelectorAll('.defeitos-vv-media-wrap--selected').forEach((n) => n.classList.remove('defeitos-vv-media-wrap--selected'));
}

function selectMediaWrap(wrap) {
  if (!wrap || !wrap.classList.contains('defeitos-vv-media-wrap')) return;
  wrap.querySelectorAll('.defeitos-vv-media-resize-handles').forEach((n) => n.remove());
  wrap.classList.add('defeitos-vv-media-wrap--selected');
  const handles = document.createElement('div');
  handles.className = 'defeitos-vv-media-resize-handles';
  handles.contentEditable = 'false';
  ['nw', 'ne', 'sw', 'se'].forEach((c) => {
    const sp = document.createElement('span');
    sp.className = `defeitos-vv-media-handle defeitos-vv-media-handle--${c}`;
    sp.dataset.corner = c;
    handles.appendChild(sp);
  });
  wrap.insertBefore(handles, wrap.firstChild);
}

function getBlockAncestor(node, root) {
  let n = node;
  if (n.nodeType === Node.TEXT_NODE) n = n.parentNode;
  while (n && n !== root) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      const tag = n.tagName.toLowerCase();
      if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'li'].includes(tag)) {
        return n;
      }
    }
    n = n.parentNode;
  }
  return null;
}

function restoreSelectionRange(range) {
  if (!range) return false;
  try {
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range.cloneRange());
    return true;
  } catch {
    return false;
  }
}

function getRestoredEditorRange(el, savedRef) {
  if (!el) return null;
  if (savedRef?.current) {
    try {
      const saved = savedRef.current.cloneRange();
      if (el.contains(saved.commonAncestorContainer)) return saved;
    } catch {
      /* ignore */
    }
  }
  const sel = window.getSelection();
  if (sel?.rangeCount > 0) {
    const r = sel.getRangeAt(0);
    if (el.contains(r.commonAncestorContainer)) return r.cloneRange();
  }
  return null;
}

function commitEditorSelection(el, range, savedRef, keepHighlight = true) {
  if (!el || !range) return;
  el.focus();
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  if (savedRef && keepHighlight) {
    savedRef.current = range.cloneRange();
  }
}

function selectionRangeForNode(node, hadHighlight) {
  const range = document.createRange();
  if (hadHighlight) {
    range.selectNodeContents(node);
  } else {
    range.selectNodeContents(node);
    range.collapse(false);
  }
  return range;
}

function wrapRangeWithStyledSpan(range, styles) {
  const span = document.createElement('span');
  Object.assign(span.style, styles);
  try {
    range.surroundContents(span);
  } catch {
    const fragment = range.extractContents();
    span.appendChild(fragment);
    range.insertNode(span);
  }
  return span;
}

function applyFontSizeToRange(el, range, sizePx) {
  const block = getBlockAncestor(range.commonAncestorContainer, el);
  if (block && HEADING_TAGS.includes(block.tagName.toLowerCase())) return null;

  if (range.collapsed) {
    const span = document.createElement('span');
    span.style.setProperty('font-size', sizePx, 'important');
    span.appendChild(document.createTextNode('\u200b'));
    range.insertNode(span);
    const after = document.createRange();
    after.setStart(span.firstChild, 1);
    after.collapse(true);
    return { node: span, caret: after };
  }

  const span = wrapRangeWithStyledSpan(range, {});
  span.style.setProperty('font-size', sizePx, 'important');
  const highlight = document.createRange();
  highlight.selectNodeContents(span);
  return { node: span, highlight, collapsed: false };
}

function detectSelectionFormats(rootEl) {
  if (!rootEl) return { heading: null, fontSize: null };
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return { heading: null, fontSize: null };

  let node = sel.getRangeAt(0).commonAncestorContainer;
  if (node.nodeType === Node.TEXT_NODE) node = node.parentNode;
  if (!rootEl.contains(node)) return { heading: null, fontSize: null };

  let heading = null;
  let fontSize = null;

  let n = node;
  while (n && n !== rootEl) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      const tag = n.tagName.toLowerCase();
      if (HEADING_TAGS.includes(tag)) {
        heading = tag;
        break;
      }
    }
    n = n.parentNode;
  }

  if (!heading) {
    n = node;
    while (n && n !== rootEl) {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const inline = n.style?.getPropertyValue('font-size');
        if (inline) {
          const px = parseInt(inline, 10);
          if (px === 14) fontSize = 'medium';
          else if (px === 12) fontSize = 'small';
          else if (px === 10) fontSize = 'xs';
          break;
        }
      }
      n = n.parentNode;
    }
    if (!fontSize) fontSize = RT_DEFAULT_FONT_SIZE_ID;
  }

  return { heading, fontSize };
}

function RtToolbarButton({ cmd, active, edge, disabled, label, shortcut, onExec, onClick, children }) {
  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    onExec(cmd);
  };

  return (
    <button
      type="button"
      className={`defeitos-vv-rt-icon-btn${active ? ' is-active' : ''}${edge ? ' defeitos-vv-rt-icon-btn--edge' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleClick}
      disabled={disabled}
      aria-label={label}
    >
      {children}
      <span className="defeitos-vv-rt-tip">
        <b>{label}</b>
        {shortcut ? <small>{shortcut}</small> : null}
      </span>
    </button>
  );
}

function RtColorMenu({ onApplyText, onApplyBg }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="defeitos-vv-rt-menu-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button type="button" className="defeitos-vv-rt-icon-btn" onMouseDown={(e) => e.preventDefault()} aria-label="Cor">
        <Palette size={16} strokeWidth={2} />
      </button>
      {open ? (
        <div className="defeitos-vv-rt-color-menu" role="menu">
          <p className="defeitos-vv-rt-color-section">Cor do texto</p>
          {RT_TEXT_COLORS.map((item) => (
            <button
              key={`text-${item.id}`}
              type="button"
              className="defeitos-vv-rt-color-item"
              onMouseDown={(e) => {
                e.preventDefault();
                onApplyText(item);
                setOpen(false);
              }}
            >
              <span
                className={`defeitos-vv-rt-color-swatch defeitos-vv-rt-color-swatch--text${item.id === 'default' ? ' is-default' : ''}`}
                style={item.id === 'default' ? undefined : { color: item.color, borderColor: item.color }}
              >
                A
              </span>
              <span>{item.label}</span>
            </button>
          ))}
          <p className="defeitos-vv-rt-color-section">Cor de fundo</p>
          {RT_BG_COLORS.map((item) => (
            <button
              key={`bg-${item.id}`}
              type="button"
              className="defeitos-vv-rt-color-item"
              onMouseDown={(e) => {
                e.preventDefault();
                onApplyBg(item);
                setOpen(false);
              }}
            >
              <span
                className={`defeitos-vv-rt-color-swatch defeitos-vv-rt-color-swatch--bg${item.id === 'default' ? ' is-default' : ''}`}
                style={item.color === 'transparent' ? undefined : { backgroundColor: item.color }}
              />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RtHeadingMenu({ onApply, activeHeading }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="defeitos-vv-rt-menu-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={`defeitos-vv-rt-icon-btn${activeHeading ? ' is-active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Título"
      >
        <Heading size={16} strokeWidth={2} />
      </button>
      {open ? (
        <div className="defeitos-vv-rt-heading-menu" role="menu">
          {RT_HEADINGS.map((item) => (
            <button
              key={item.tag}
              type="button"
              className={`defeitos-vv-rt-heading-item${activeHeading === item.tag ? ' is-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onApply(item.tag);
                setOpen(false);
              }}
            >
              <span className="defeitos-vv-rt-heading-badge">{item.preview}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RtFontSizeMenu({ onApply, activeFontSize }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="defeitos-vv-rt-menu-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className={`defeitos-vv-rt-icon-btn${activeFontSize ? ' is-active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        aria-label="Tamanho da fonte"
      >
        <ALargeSmall size={16} strokeWidth={2} />
      </button>
      {open ? (
        <div className="defeitos-vv-rt-fontsize-menu" role="menu">
          {RT_FONT_SIZES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`defeitos-vv-rt-fontsize-item${activeFontSize === item.id ? ' is-active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onApply(item);
                setOpen(false);
              }}
            >
              <span className="defeitos-vv-rt-fontsize-badge">{item.preview}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EmbedMediaModal({ open, onClose, onApply }) {
  const [tab, setTab] = useState('geral');
  const [url, setUrl] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (open) {
      setTab('geral');
      setUrl('');
      setCode('');
      setErr('');
    }
  }, [open]);

  if (!open) return null;

  const apply = () => {
    setErr('');
    let iframeHtml = '';
    if (tab === 'geral') {
      iframeHtml = youtubeUrlToIframeHtml(url);
      if (!iframeHtml) setErr('Cole uma URL válida do YouTube (watch, youtu.be, embed ou shorts).');
    } else {
      iframeHtml = extractYoutubeIframeFromEmbedHtml(code);
      if (!iframeHtml) setErr('Cole um iframe do YouTube (youtube.com ou youtube-nocookie.com).');
    }
    if (!iframeHtml) return;
    onApply(buildMediaWrapHtml(iframeHtml));
    onClose();
  };

  return (
    <div
      className="defeitos-vv-embed-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="defeitos-vv-embed-modal" role="dialog" aria-labelledby="defeitos-vv-embed-title" onMouseDown={(e) => e.stopPropagation()}>
        <div className="defeitos-vv-embed-modal-header">
          <h3 id="defeitos-vv-embed-title" className="defeitos-vv-embed-modal-title">
            Inserir/Editar Mídia
          </h3>
          <button type="button" className="defeitos-vv-embed-modal-close" onClick={onClose} aria-label="Fechar">
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <div className="defeitos-vv-embed-modal-body">
          <div className="defeitos-vv-embed-tabs" role="tablist" aria-label="Tipo de mídia">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'geral'}
              className={`defeitos-vv-embed-tab${tab === 'geral' ? ' defeitos-vv-embed-tab--active' : ''}`}
              onClick={() => {
                setTab('geral');
                setErr('');
              }}
            >
              Geral
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'embutido'}
              className={`defeitos-vv-embed-tab${tab === 'embutido' ? ' defeitos-vv-embed-tab--active' : ''}`}
              onClick={() => {
                setTab('embutido');
                setErr('');
              }}
            >
              Embutido
            </button>
          </div>
          <div className="defeitos-vv-embed-panel">
            {tab === 'geral' ? (
              <>
                <label className="defeitos-vv-embed-label" htmlFor="defeitos-vv-yt-url">
                  URL do YouTube
                </label>
                <p className="defeitos-vv-embed-hint">Cole o link do vídeo (página do vídeo, youtu.be ou shorts).</p>
                <input
                  id="defeitos-vv-yt-url"
                  type="url"
                  className="defeitos-vv-embed-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=…"
                  autoComplete="off"
                />
              </>
            ) : (
              <>
                <label className="defeitos-vv-embed-label" htmlFor="defeitos-vv-embed-code">
                  Código de incorporação
                </label>
                <p className="defeitos-vv-embed-hint">Cole seu código abaixo de incorporação:</p>
                <textarea
                  id="defeitos-vv-embed-code"
                  className="defeitos-vv-embed-textarea"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={'<iframe width="560" height="315" src="https://www.youtube.com/embed/..." ...></iframe>'}
                  spellCheck={false}
                />
              </>
            )}
            {err ? (
              <p className="defeitos-vv-embed-hint" style={{ color: '#b91c1c' }}>
                {err}
              </p>
            ) : null}
          </div>
        </div>
        <div className="defeitos-vv-embed-modal-footer">
          <button type="button" className="defeitos-vv-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="defeitos-vv-btn-embed-primary" onClick={apply}>
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

export function RichTextModal({
  open,
  initialHtml,
  title,
  onClose,
  onSave,
  saving,
  tabs,
  activeTab,
  onTabChange,
  normalizeHtml,
  embedded = false,
  htmlGetterRef = null,
}) {
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const [activeCmds, setActiveCmds] = useState({});
  const [activeHeading, setActiveHeading] = useState(null);
  const [activeFontSize, setActiveFontSize] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const finalizeHtml = useCallback(
    (raw) => {
      const cleaned = stripRichTextEditorArtifacts(raw);
      return typeof normalizeHtml === 'function' ? normalizeHtml(cleaned) : cleaned;
    },
    [normalizeHtml]
  );

  useEffect(() => {
    if (!htmlGetterRef) return undefined;
    htmlGetterRef.current = () => finalizeHtml(editorRef.current?.innerHTML || '');
    return () => { htmlGetterRef.current = null; };
  }, [htmlGetterRef, finalizeHtml]);

  const updateActiveStates = useCallback(() => {
    const next = {};
    RT_FORMAT_CMDS.forEach((cmd) => {
      try {
        next[cmd] = document.queryCommandState(cmd);
      } catch {
        next[cmd] = false;
      }
    });
    setActiveCmds(next);

    const el = editorRef.current;
    if (el) {
      const { heading, fontSize } = detectSelectionFormats(el);
      setActiveHeading(heading);
      setActiveFontSize(heading ? null : fontSize);
    }

    try {
      setCanUndo(document.queryCommandEnabled('undo'));
      setCanRedo(document.queryCommandEnabled('redo'));
    } catch {
      setCanUndo(false);
      setCanRedo(false);
    }
  }, []);

  const captureEditorSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) {
      savedSelectionRef.current = null;
      return;
    }
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) {
        const saved = savedSelectionRef.current;
        if (!r.collapsed || !saved || saved.collapsed) {
          savedSelectionRef.current = r.cloneRange();
        }
      }
    }
  }, []);

  const restoreEditorSelection = useCallback(() => {
    const el = editorRef.current;
    if (!el) return false;
    el.focus();
    return restoreSelectionRange(savedSelectionRef.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    try {
      document.execCommand('defaultParagraphSeparator', false, 'p');
    } catch {
      /* ignore */
    }
    const el = editorRef.current;
    if (el) {
      el.innerHTML = initialHtml || '';
      enhanceRichTextMediaInEditor(el);
      el.focus();
    }
  }, [open, initialHtml]);

  useEffect(() => {
    if (!open) return undefined;
    const ed = editorRef.current;
    if (!ed) return undefined;

    const startResize = (e, handleEl) => {
      const wrap = handleEl.closest('.defeitos-vv-media-wrap');
      if (!wrap || !ed.contains(wrap)) return;
      e.preventDefault();
      e.stopPropagation();
      const corner = handleEl.dataset.corner;
      const startX = e.clientX;
      const rect = wrap.getBoundingClientRect();
      const startW = rect.width;
      const startMl = parseFloat(wrap.style.marginLeft) || 0;
      const edRect = ed.getBoundingClientRect();
      const maxW = Math.max(200, edRect.width - 32);
      const minW = 200;

      const onMove = (ev) => {
        const dx = ev.clientX - startX;
        let newW = startW;
        let newMl = startMl;
        if (corner === 'se' || corner === 'ne') {
          newW = Math.min(maxW, Math.max(minW, startW + dx));
        } else {
          newW = Math.min(maxW, Math.max(minW, startW - dx));
          newMl = startMl + (startW - newW);
        }
        wrap.style.width = `${Math.round(newW)}px`;
        wrap.style.maxWidth = '100%';
        wrap.style.marginLeft = `${Math.round(newMl)}px`;
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    const onMouseDown = (e) => {
      const handle = e.target.closest?.('.defeitos-vv-media-handle');
      if (handle && ed.contains(handle)) {
        startResize(e, handle);
        return;
      }
      const wrap = e.target.closest?.('.defeitos-vv-media-wrap');
      if (wrap && ed.contains(wrap)) {
        if (wrap.classList.contains('defeitos-vv-media-wrap--selected')) {
          e.preventDefault();
          clearMediaSelection(ed);
          placeCaretInBlockAfterMedia(ed, wrap);
          return;
        }
        clearMediaSelection(ed);
        selectMediaWrap(wrap);
        e.preventDefault();
        return;
      }
      if (e.target === ed) {
        const last = ed.lastElementChild;
        if (last?.classList?.contains('defeitos-vv-media-wrap')) {
          placeCaretInBlockAfterMedia(ed, last);
        }
      }
      clearMediaSelection(ed);
    };

    const onKeyDown = (e) => {
      const selectedWrap = ed.querySelector('.defeitos-vv-media-wrap--selected');
      if (!selectedWrap) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        clearMediaSelection(ed);
        placeCaretInBlockAfterMedia(ed, selectedWrap);
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        clearMediaSelection(ed);
        placeCaretInBlockAfterMedia(ed, selectedWrap);
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        clearMediaSelection(ed);
        placeCaretBeforeMedia(ed, selectedWrap);
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const prev = selectedWrap.previousSibling;
        const next = selectedWrap.nextSibling;
        selectedWrap.remove();
        clearMediaSelection(ed);
        if (next && next.nodeType === Node.ELEMENT_NODE && isEmptyEditableBlock(next)) {
          const range = document.createRange();
          range.setStart(next, 0);
          range.collapse(true);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        } else if (prev && prev.nodeType === Node.ELEMENT_NODE) {
          const range = document.createRange();
          range.selectNodeContents(prev);
          range.collapse(false);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          ed.focus();
        }
      }
    };

    ed.addEventListener('mousedown', onMouseDown);
    ed.addEventListener('keydown', onKeyDown);
    return () => {
      ed.removeEventListener('mousedown', onMouseDown);
      ed.removeEventListener('keydown', onKeyDown);
    };
  }, [open, initialHtml]);

  useEffect(() => {
    if (!open) setEmbedOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onSelectionChange = () => {
      captureEditorSelection();
      const el = editorRef.current;
      if (!el) return;
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer)) {
        updateActiveStates();
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [open, updateActiveStates, captureEditorSelection]);

  const exec = (cmd, value = null) => {
    editorRef.current?.focus();
    try {
      document.execCommand(cmd, false, value);
    } catch {
      /* ignore */
    }
    updateActiveStates();
  };

  const handleUndo = () => {
    exec('undo');
  };

  const handleRedo = () => {
    exec('redo');
  };

  const insertLink = () => {
    editorRef.current?.focus();
    const url = window.prompt('URL do link:', 'https://');
    if (!url) return;
    exec('createLink', url.trim());
  };

  const applyInlineCode = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;
    const codeEl = document.createElement('code');
    codeEl.appendChild(range.extractContents());
    range.insertNode(codeEl);
    range.setStartAfter(codeEl);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    updateActiveStates();
  };

  const applyTextColor = (item) => {
    const el = editorRef.current;
    if (!el) return;
    restoreEditorSelection();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;

    const color = item.id === 'default' ? '#18181b' : item.color;

    if (range.collapsed) {
      try {
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('foreColor', false, color);
      } catch {
        /* ignore */
      }
      updateActiveStates();
      return;
    }

    const span = wrapRangeWithStyledSpan(range, { color });
    const after = document.createRange();
    after.selectNodeContents(span);
    after.collapse(false);
    sel.removeAllRanges();
    sel.addRange(after);
    captureEditorSelection();
    updateActiveStates();
  };

  const applyBgColor = (item) => {
    const el = editorRef.current;
    if (!el) return;
    restoreEditorSelection();
    if (item.id === 'default') {
      try {
        document.execCommand('hiliteColor', false, '#ffffff');
      } catch {
        document.execCommand('backColor', false, '#ffffff');
      }
      updateActiveStates();
      return;
    }
    try {
      document.execCommand('hiliteColor', false, item.color);
    } catch {
      document.execCommand('backColor', false, item.color);
    }
    updateActiveStates();
  };

  const applyHeading = (tag) => {
    const el = editorRef.current;
    if (!el) return;
    const range = getRestoredEditorRange(el, savedSelectionRef);
    if (!range) return;

    const hadHighlight = !range.collapsed;
    commitEditorSelection(el, range, savedSelectionRef);

    const sel = window.getSelection();
    let block = getBlockAncestor(range.commonAncestorContainer, el);

    if (block && block.tagName.toLowerCase() === tag) {
      const p = document.createElement('p');
      p.innerHTML = block.innerHTML;
      block.parentNode.replaceChild(p, block);
      commitEditorSelection(el, selectionRangeForNode(p, hadHighlight), savedSelectionRef);
      updateActiveStates();
      return;
    }

    try {
      document.execCommand('styleWithCSS', false, true);
    } catch {
      /* ignore */
    }

    if (document.execCommand('formatBlock', false, tag) || document.execCommand('formatBlock', false, `<${tag}>`)) {
      block = getBlockAncestor(sel.getRangeAt(0).commonAncestorContainer, el);
      if (block) {
        commitEditorSelection(el, selectionRangeForNode(block, hadHighlight), savedSelectionRef);
      }
      updateActiveStates();
      return;
    }

    if (!block) {
      const heading = document.createElement(tag);
      if (!range.collapsed) {
        heading.appendChild(range.extractContents());
      } else {
        heading.appendChild(document.createElement('br'));
      }
      range.insertNode(heading);
      block = heading;
    } else if (block.tagName.toLowerCase() !== tag) {
      const heading = document.createElement(tag);
      heading.innerHTML = block.innerHTML;
      block.parentNode.replaceChild(heading, block);
      block = heading;
    }

    commitEditorSelection(el, selectionRangeForNode(block, hadHighlight), savedSelectionRef);
    updateActiveStates();
  };

  const applyFontSize = (item) => {
    const el = editorRef.current;
    if (!el) return;
    const range = getRestoredEditorRange(el, savedSelectionRef);
    if (!range) return;

    const hadHighlight = !range.collapsed;
    commitEditorSelection(el, range, savedSelectionRef);

    const sel = window.getSelection();
    const activeRange = sel.rangeCount > 0 ? sel.getRangeAt(0) : range;
    const result = applyFontSizeToRange(el, activeRange, item.size);
    if (!result) return;

    if (result.highlight && hadHighlight) {
      commitEditorSelection(el, result.highlight, savedSelectionRef);
    } else if (result.caret) {
      commitEditorSelection(el, result.caret, savedSelectionRef, false);
    } else if (result.node) {
      commitEditorSelection(el, selectionRangeForNode(result.node, hadHighlight), savedSelectionRef);
    }

    updateActiveStates();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key === 'k') {
        e.preventDefault();
        insertLink();
      } else if (key === 'e') {
        e.preventDefault();
        applyInlineCode();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  const openEmbedModal = () => {
    captureEditorSelection();
    setEmbedOpen(true);
  };

  const insertMediaHtml = (wrapHtml) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    try {
      let range = null;
      if (savedSelectionRef.current) {
        try {
          range = savedSelectionRef.current.cloneRange();
        } catch {
          range = null;
        }
      }
      if (!range || !el.contains(range.commonAncestorContainer)) {
        range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
      }
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand('insertHTML', false, wrapHtml);
    } catch {
      /* ignore */
    }
    savedSelectionRef.current = null;
    enhanceRichTextMediaInEditor(el);
    clearMediaSelection(el);
    const wraps = el.querySelectorAll('.defeitos-vv-media-wrap');
    const lastWrap = wraps[wraps.length - 1];
    if (lastWrap) {
      ensureEditableAfterMedia(el, lastWrap);
    }
  };

  const handleSave = () => {
    const raw = editorRef.current?.innerHTML || '';
    onSave(finalizeHtml(raw));
  };

  const handleTabClick = (tabId) => {
    if (!onTabChange || tabId === activeTab || saving) return;
    const raw = editorRef.current?.innerHTML || '';
    onTabChange(tabId, finalizeHtml(raw));
  };

  if (!open) return null;

  const dialog = (
    <div
      className={['defeitos-vv-rt-modal', embedded ? 'dv2-rt-embedded' : '', !embedded && hasTabs ? 'defeitos-vv-rt-modal--tabs' : ''].filter(Boolean).join(' ')}
      role={embedded ? undefined : 'dialog'}
      aria-labelledby={embedded ? undefined : 'defeitos-vv-rt-modal-title'}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {embedded ? null : (
        <header className="defeitos-vv-rt-modal-head">
          <h1 id="defeitos-vv-rt-modal-title" className="defeitos-vv-rt-modal-title">
            {title}
          </h1>
          <button
            type="button"
            className="defeitos-vv-rt-icon-btn defeitos-vv-rt-icon-btn--edge"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar"
          >
            <X size={16} strokeWidth={2} />
            <span className="defeitos-vv-rt-tip">
              <b>Fechar</b>
            </span>
          </button>
        </header>
      )}

        {hasTabs && !embedded ? (
          <div className="defeitos-vv-rt-tabs" role="tablist" aria-label="Seções do documento">
            {tabs.map((tab) => {
              const id = tab?.id;
              const label = tab?.label || id;
              const selected = id === activeTab;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={['defeitos-vv-rt-tab', selected ? 'defeitos-vv-rt-tab--active' : ''].filter(Boolean).join(' ')}
                  onClick={() => handleTabClick(id)}
                  disabled={saving}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="defeitos-vv-rt-toolbar">
          <RtToolbarButton label="Desfazer" shortcut="Ctrl+Z" disabled={!canUndo} onClick={handleUndo}>
            <Undo2 size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtToolbarButton label="Refazer" shortcut="Ctrl+Y" disabled={!canRedo} onClick={handleRedo}>
            <Redo2 size={16} strokeWidth={2} />
          </RtToolbarButton>
          <span className="defeitos-vv-rt-toolbar-div" aria-hidden />
          <RtToolbarButton cmd="bold" active={activeCmds.bold} label="Negrito" shortcut="Ctrl+B" onExec={exec}>
            <Bold size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtToolbarButton cmd="italic" active={activeCmds.italic} label="Itálico" shortcut="Ctrl+I" onExec={exec}>
            <Italic size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtToolbarButton cmd="underline" active={activeCmds.underline} label="Sublinhado" shortcut="Ctrl+U" onExec={exec}>
            <Underline size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtToolbarButton cmd="strikeThrough" active={activeCmds.strikeThrough} label="Tachado" shortcut="Ctrl+Shift+X" onExec={exec}>
            <Strikethrough size={16} strokeWidth={2} />
          </RtToolbarButton>
          <span className="defeitos-vv-rt-toolbar-div" aria-hidden />
          <RtToolbarButton cmd="insertUnorderedList" active={activeCmds.insertUnorderedList} label="Lista" onExec={exec}>
            <List size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtToolbarButton cmd="insertOrderedList" active={activeCmds.insertOrderedList} label="Lista numerada" onExec={exec}>
            <ListOrdered size={16} strokeWidth={2} />
          </RtToolbarButton>
          <span className="defeitos-vv-rt-toolbar-div" aria-hidden />
          <RtToolbarButton label="Link" shortcut="Ctrl+K" onClick={insertLink}>
            <Link2 size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtToolbarButton label="Código" shortcut="Ctrl+E" onClick={applyInlineCode}>
            <Code2 size={16} strokeWidth={2} />
          </RtToolbarButton>
          <RtColorMenu onApplyText={applyTextColor} onApplyBg={applyBgColor} />
          <RtHeadingMenu onApply={applyHeading} activeHeading={activeHeading} />
          {!activeHeading ? <RtFontSizeMenu onApply={applyFontSize} activeFontSize={activeFontSize} /> : null}
          <span className="defeitos-vv-rt-toolbar-div" aria-hidden />
          <RtToolbarButton label="Inserir vídeo" edge onClick={openEmbedModal}>
            <Video size={16} strokeWidth={2} />
          </RtToolbarButton>
        </div>

        <div className="defeitos-vv-rt-scroll">
          <div
            ref={editorRef}
            className="defeitos-vv-rt-editor"
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onInput={updateActiveStates}
            onMouseUp={captureEditorSelection}
            onKeyUp={captureEditorSelection}
          />
        </div>

        {embedded ? null : (
        <div className="user-form-modern-footer defeitos-vv-rt-modal-foot">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
        )}
      </div>
    );

  return (
    <>
      {embedded ? dialog : (
        <div className="defeitos-vv-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
          {dialog}
        </div>
      )}
      <EmbedMediaModal open={embedOpen} onClose={() => setEmbedOpen(false)} onApply={insertMediaHtml} />
    </>
  );
}
